import { useTranslate } from '@/components/Helpers/Translate'
import type { MarketExceptionType } from '@/constants/MarketExceptions/MarketExceptionTypes'
import type { JeevesPricingType } from '@/lib/services/Jeeves/pricing/getProductPricing'
import type { CustomerType } from '@/store/types/user'
import useGlobalStore from '@/store/useGlobalStore'
import { getTerms } from '../services/useGetTerms'

type BasePriceHandlerProps = {
  currency?: string
  forCustomerType?: CustomerType
  returnPriceAsNumber?: boolean
  quantity?: number
  subscriptionDiscount?: number
  returnFreeIfPriceIsZero?: boolean
}

type PriceBasedProps = {
  price: number
  pricingData?: never
} & BasePriceHandlerProps

type PricingDataBasedProps = {
  price?: never
  pricingData: JeevesPricingType | null
} & BasePriceHandlerProps

type PriceHandlerProps = PriceBasedProps | PricingDataBasedProps

const getTaxAmount = ({
  marketExceptions,
  terms,
  quantity,
}: {
  marketExceptions: MarketExceptionType | null
  terms: JeevesPricingType['levels'][number]['terms'] | null
  quantity: number
}) => {
  if (!marketExceptions) return 0
  if (!marketExceptions?.tax?.inclusive) return 0
  if (!terms) return 0
  if (marketExceptions?.tax?.noFrontendTaxCalculation) return 0

  return terms.taxEach.amount * quantity
}

export const usePriceHandler = () => {
  const {
    app: { marketExceptions, locale },
    user: { customerType },
  } = useGlobalStore()
  const { translate } = useTranslate()

  const currencyCode = marketExceptions?.currencyCode

  const priceHandler = ({
    price,
    currency = currencyCode,
    forCustomerType = customerType,
    pricingData,
    returnPriceAsNumber,
    quantity = 1,
    subscriptionDiscount = 0,
    returnFreeIfPriceIsZero = true,
  }: PriceHandlerProps): string | number => {
    const terms = pricingData
      ? getTerms({
          forCustomerType: forCustomerType || 'Customer',
          pricingData,
        })
      : null

    if (price === 0 && returnFreeIfPriceIsZero) return translate('free')

    let basePrice = terms?.priceEach || price || 0

    if (typeof basePrice === 'string') {
      const parsedNumber = Number(basePrice)

      if (!Number.isNaN(parsedNumber)) {
        basePrice = parsedNumber
      }
    }

    if (typeof basePrice !== 'number') return basePrice

    const priceWithDiscount = basePrice - subscriptionDiscount

    const preTaxTotal = priceWithDiscount * quantity

    const taxAmount = getTaxAmount({
      marketExceptions,
      terms,
      quantity,
    })

    const finalPrice = preTaxTotal + taxAmount

    if (returnPriceAsNumber) {
      return finalPrice
    }

    if (currency === 'JPY') {
      const formattedPrice = Intl.NumberFormat(locale, {
        style: 'decimal',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(finalPrice)

      const currencySymbol = locale?.startsWith('ja') ? '円' : '¥'
      return `${formattedPrice}${currencySymbol}`
    }

    return Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(finalPrice)
  }

  return { priceHandler }
}
